/**
 * PROPRIETARY AND CONFIDENTIAL
 * Copyright © 2026 Contractor Co-Pilot. All Rights Reserved.
 */
"use client";
import {
  FeaturePageTemplate,
  type FeaturePageConfig,
} from "@/components/landing/feature-page-template";
import {
  Search,
  Shield,
  MessageSquare,
  Zap,
  FileText,
  Settings2,
  Calculator,
  DollarSign,
  Briefcase,
  Ruler,
  Sparkles,
} from "lucide-react";
import { AI_TOOL_COUNT } from "@/content/landing/constants";

const config: FeaturePageConfig = {
  badge: "AI Assistant (Hank)",
  title: "Ask one assistant",
  titleHighlight: "about your real jobs.",
  description: `${AI_TOOL_COUNT} tools. Ask about your costs, estimates, margins, projects — in plain English. Hank uses your actual data and suggests actions you confirm so teams can move faster with better context.`,

  featureGrid: {
    title: "What Hank can do",
    items: [
      {
        icon: Search,
        title: "Query Your Data",
        description:
          "Ask about costs, estimates, projects, clients, suppliers — in plain English.",
      },
      {
        icon: Shield,
        title: "Audit Estimates",
        description:
          "Check margins, flag missing items, compare against similar past work.",
      },
      {
        icon: FileText,
        title: "Analyze Documents",
        description:
          "Upload blueprints, spreadsheets, and PDFs so Hank can suggest useful structure and context for review.",
      },
      {
        icon: Zap,
        title: "Daily Briefings",
        description:
          "Say 'good morning' — get tasks, schedule, and suggestions instantly.",
      },
      {
        icon: MessageSquare,
        title: "Draft Communications",
        description:
          "Draft emails and messages with the right project context.",
      },
      {
        icon: Settings2,
        title: "Move Work Forward",
        description:
          "Create next steps and keep jobs moving without bouncing between screens.",
      },
    ],
  },

  workflow: {
    title: "How teams use Hank in real workflows",
    description:
      "Use Hank to speed analysis and prep work while keeping final decisions in your hands.",
    steps: [
      {
        label: "Ask",
        title: "Start with a business question",
        description:
          "Ask questions about margins, open estimates, project status, or cost trends and get context-aware answers quickly.",
        details: [
          "Plain-language questions",
          "Business-context-aware responses",
          "Faster analysis for managers and estimators",
          "Reduced manual data hunting",
        ],
      },
      {
        label: "Focus",
        title: "Keep the conversation tied to the right job",
        description:
          "Anchor conversations to the client, estimate, or project you are actively working so responses stay relevant.",
        details: [
          "Job-specific context support",
          "Cleaner thread continuity",
          "Less ambiguity in responses",
          "Easier handoff between team members",
        ],
      },
      {
        label: "Act",
        title: "Review recommendations, then approve",
        description:
          "Use Hank to draft next steps and suggested actions, then confirm what should happen so control remains with your team.",
        details: [
          "Human confirmation before action",
          "Task and follow-up support",
          "Operational momentum from chat",
          "Safer AI-assisted execution",
        ],
      },
      {
        label: "Improve",
        title: "Build better context over time",
        description:
          "As teams build usage history, Hank can provide more consistent support for recurring operational questions.",
        details: [
          "Conversation history support",
          "Reusable insights across similar jobs",
          "History-aware assistance",
          "Better continuity week over week",
        ],
      },
    ],
  },

  capabilities: [
    {
      icon: Search,
      title: "Cost Matching & Pricing Intelligence",
      description:
        "Use Hank to speed estimate review by surfacing likely cost matches and highlighting unusual pricing patterns.",
      bullets: [
        "Faster pricing review",
        "Similarity context from prior work",
        "Anomaly awareness before send",
        "Stronger bid confidence",
      ],
    },
    {
      icon: Shield,
      title: "Estimate Auditing",
      description:
        "Run a pre-send quality check to catch issues before a proposal reaches the client.",
      bullets: [
        "Margin-awareness support",
        "Scope-gap identification",
        "Historical comparison context",
        "Pre-send risk reduction",
      ],
    },
    {
      icon: Zap,
      title: "Quick Daily Workflows",
      description:
        "Use shortcut interactions for recurring actions like daily check-ins, quick tasks, and operational notes.",
      bullets: [
        "Daily priorities at a glance",
        "Fast task and note creation",
        "Less tab switching",
        "Smoother morning workflow",
      ],
    },
    {
      icon: MessageSquare,
      title: "Conversation Management",
      description:
        "Keep multiple conversations organized so different jobs and decision threads do not get mixed.",
      bullets: [
        "Multi-thread conversation support",
        "History and naming controls",
        "Cleaner context separation by topic",
        "Better team communication continuity",
      ],
    },
  ],

  connections: {
    title: "How Hank supports the rest of your workflow",
    items: [
      {
        icon: Calculator,
        flow: "Hank → Estimates",
        description:
          "Use Hank to accelerate estimate analysis and reduce pre-send mistakes.",
      },
      {
        icon: DollarSign,
        flow: "Hank → Cost Database",
        description:
          "Explore cost context quickly to support pricing decisions in active bids.",
      },
      {
        icon: Briefcase,
        flow: "Hank → Projects",
        description:
          "Get faster project and pipeline insights to guide operational next steps.",
      },
    ],
  },

  relatedFeatures: [
    {
      label: "Estimates & Takeoffs",
      href: "/features/estimates",
      icon: Calculator,
    },
    {
      label: "Cost Database",
      href: "/features/cost-database",
      icon: DollarSign,
    },
    {
      label: "Projects & Services",
      href: "/features/project-management",
      icon: Briefcase,
    },
    {
      label: "Solutions by Trade",
      href: "/solutions",
      icon: Ruler,
    },
    {
      label: "AI for General Contractors",
      href: "/ai/for-general-contractors",
      icon: Sparkles,
    },
    {
      label: "AI for HVAC Contractors",
      href: "/ai/for-hvac-contractors",
      icon: Sparkles,
    },
  ],
};

export function AIAssistantPageClient() {
  return <FeaturePageTemplate config={config} />;
}
